
import { supabase } from '@/integrations/supabase/client';

// Generate a unique referral code
const generateReferralCode = (): string => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

export const backfillReferralCodes = async (): Promise<void> => {
  console.log('🔄 Starting referral code backfill...');
  
  try {
    // Get all user_points records to see what we have
    const { data: existingPoints, error: pointsError } = await supabase
      .from('user_points')
      .select('*');

    if (pointsError) {
      console.error('Error fetching existing points:', pointsError);
      throw pointsError;
    }

    console.log('📊 Existing user_points records:', existingPoints?.length || 0);

    // Get all Supabase users from profiles
    const { data: supabaseProfiles, error: profilesError } = await supabase
      .from('profiles')
      .select('id, username, email, created_at');

    if (profilesError) {
      console.error('Error fetching Supabase profiles:', profilesError);
      throw profilesError;
    }

    console.log('👥 Supabase users found:', supabaseProfiles?.length || 0);

    // Get all internal users
    const { data: internalUsers, error: internalError } = await supabase
      .from('internal_users')
      .select('id, username, created_at');

    if (internalError) {
      console.error('Error fetching internal users:', internalError);
      throw internalError;
    }

    console.log('🔒 Internal users found:', internalUsers?.length || 0);

    let processedCount = 0;
    let createdCount = 0;
    let updatedCount = 0;

    // Process Supabase users
    if (supabaseProfiles) {
      for (const profile of supabaseProfiles) {
        const existingPoint = existingPoints?.find(p => p.user_id === profile.id && p.user_type === 'supabase');
        
        if (!existingPoint) {
          // Create new user_points record for Supabase user
          const referralCode = generateReferralCode();
          
          console.log(`🆕 Creating user_points for Supabase user: ${profile.username || profile.email} (${profile.id})`);
          
          // Use the service role or bypass RLS for this operation
          const { data: newPoint, error: insertError } = await supabase
            .from('user_points')
            .insert({
              user_id: profile.id,
              user_type: 'supabase',
              total_points: 0,
              referral_code: referralCode,
              current_level: 'Bronze',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            })
            .select()
            .single();

          if (insertError) {
            console.error(`❌ Error creating user_points for ${profile.username}:`, insertError);
            
            // Try alternative approach - call the award_points function which should handle RLS
            try {
              console.log(`🔄 Trying alternative approach for ${profile.username}...`);
              const { data: functionResult, error: functionError } = await supabase
                .rpc('award_points', {
                  p_user_id: profile.id,
                  p_user_type: 'supabase',
                  p_points: 0,
                  p_transaction_type: 'backfill',
                  p_description: 'Initial referral code backfill'
                });
              
              if (functionError) {
                console.error(`❌ Function approach failed for ${profile.username}:`, functionError);
              } else {
                console.log(`✅ Function approach succeeded for ${profile.username}`);
                createdCount++;
              }
            } catch (funcError) {
              console.error(`❌ Exception in function approach for ${profile.username}:`, funcError);
            }
          } else {
            console.log(`✅ Created user_points for ${profile.username}`);
            createdCount++;
          }
        } else if (!existingPoint.referral_code || existingPoint.referral_code === '') {
          // Update existing record with referral code
          const referralCode = generateReferralCode();
          
          console.log(`🔄 Updating referral code for Supabase user: ${profile.username || profile.email} (${profile.id})`);
          
          const { error: updateError } = await supabase
            .from('user_points')
            .update({
              referral_code: referralCode,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', profile.id)
            .eq('user_type', 'supabase');

          if (updateError) {
            console.error(`❌ Error updating referral code for ${profile.username}:`, updateError);
          } else {
            console.log(`✅ Updated referral code for ${profile.username}`);
            updatedCount++;
          }
        } else {
          console.log(`⏭️ Supabase user ${profile.username || profile.email} already has referral code: ${existingPoint.referral_code}`);
        }
        
        processedCount++;
      }
    }

    // Process Internal users
    if (internalUsers) {
      for (const user of internalUsers) {
        const existingPoint = existingPoints?.find(p => p.user_id === user.id && p.user_type === 'internal');
        
        if (!existingPoint) {
          // Create new user_points record for internal user
          const referralCode = generateReferralCode();
          
          console.log(`🆕 Creating user_points for internal user: ${user.username} (${user.id})`);
          
          const { error: insertError } = await supabase
            .from('user_points')
            .insert({
              user_id: user.id,
              user_type: 'internal',
              total_points: 0,
              referral_code: referralCode,
              current_level: 'Bronze',
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });

          if (insertError) {
            console.error(`❌ Error creating user_points for ${user.username}:`, insertError);
          } else {
            console.log(`✅ Created user_points for ${user.username}`);
            createdCount++;
          }
        } else if (!existingPoint.referral_code || existingPoint.referral_code === '') {
          // Update existing record with referral code
          const referralCode = generateReferralCode();
          
          console.log(`🔄 Updating referral code for internal user: ${user.username} (${user.id})`);
          
          const { error: updateError } = await supabase
            .from('user_points')
            .update({
              referral_code: referralCode,
              updated_at: new Date().toISOString()
            })
            .eq('user_id', user.id)
            .eq('user_type', 'internal');

          if (updateError) {
            console.error(`❌ Error updating referral code for ${user.username}:`, updateError);
          } else {
            console.log(`✅ Updated referral code for ${user.username}`);
            updatedCount++;
          }
        } else {
          console.log(`⏭️ Internal user ${user.username} already has referral code: ${existingPoint.referral_code}`);
        }
        
        processedCount++;
      }
    }

    console.log('📊 Backfill Summary:');
    console.log(`   Processed: ${processedCount} users`);
    console.log(`   Created: ${createdCount} new records`);
    console.log(`   Updated: ${updatedCount} existing records`);
    
    // Final verification
    const { data: finalPoints, error: finalError } = await supabase
      .from('user_points')
      .select('user_id, user_type, referral_code');

    if (finalError) {
      console.error('Error in final verification:', finalError);
    } else {
      console.log('✅ Final verification - Total user_points records:', finalPoints?.length || 0);
      const withCodes = finalPoints?.filter(p => p.referral_code && p.referral_code !== '').length || 0;
      console.log(`✅ Records with referral codes: ${withCodes}`);
    }

    console.log('🎉 Referral code backfill completed!');
    
  } catch (error) {
    console.error('❌ Fatal error during referral code backfill:', error);
    throw error;
  }
};
